import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * МЕСЯЧНЫЙ КАЛЕНДАРИК В МЕНЮ ПОД ПУНКТОМ «КАЛЕНДАРЬ» (`s310`, владелец 09.09.2026
 * по скрину): сама ссылка «Календарь» — переход на страницу журнала, клик по
 * дате здесь — открытие сразу нужного дня. День едет адресом
 * `/zapis/den?date=…` — он живёт в URL с `s307`, страница читает его сама.
 *
 * ⛔ Календарик НЕ ходит за данными (тот же довод, что у `KalendarMesyaca` на
 * самой странице): он выбирает день, и только — подсветка занятости стоила бы
 * тридцати запросов при каждом открытии меню.
 *
 * ⛔ НЕДЕЛЯ С ПОНЕДЕЛЬНИКА: `Date#getDay()` считает воскресенье нулём — взяв
 * его как есть, получим субботу под заголовком «пн» (урок `KalendarMesyaca`).
 *
 * Компонент свой, а не переиспользованный `KalendarMesyaca`: тот свёрстан
 * словарём и светлой шкурой журнала, здесь — тёмный сайдбар и навигация
 * адресом; общая у них только сетка из тридцати кнопок.
 */
export function MiniKalendarMenyu({ onClose }: { onClose?: () => void }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();

  const segodnya = new Date();
  const kluchDnya = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Выбранный день подсвечивается только на самой странице журнала: адрес —
  // единственная правда о нём; на чужих экранах выбранного дня нет.
  const vybran = pathname === '/zapis/den' ? (params.get('date') ?? kluchDnya(segodnya)) : null;

  const [mesyac, setMesyac] = useState(() => {
    const osnova = vybran ? new Date(`${vybran}T12:00:00`) : segodnya;
    return new Date(osnova.getFullYear(), osnova.getMonth(), 1);
  });

  const sdvig = (new Date(mesyac.getFullYear(), mesyac.getMonth(), 1).getDay() + 6) % 7;
  const dney = new Date(mesyac.getFullYear(), mesyac.getMonth() + 1, 0).getDate();
  const kletki: (number | null)[] = [
    ...Array.from({ length: sdvig }, () => null),
    ...Array.from({ length: dney }, (_, i) => i + 1),
  ];

  const kluch = (chislo: number) =>
    `${mesyac.getFullYear()}-${String(mesyac.getMonth() + 1).padStart(2, '0')}-${String(chislo).padStart(2, '0')}`;
  const segodnyaKluch = kluchDnya(segodnya);

  const otkrytDen = (den: string) => {
    /*
     * ⛔ КАЛЕНДАРЬ НЕСЁТ ВЫБРАННЫЙ ФИЛИАЛ (владелец, 09.09.2026, `s311`: «смена
     * филиала и календарь должны менять на соотв. филиал — я поменял на СПб, а
     * календарь открывает МСК»).
     *
     * Ссылка строилась из одной даты и ЗАТИРАЛА филиал в адресе. Дальше журнал,
     * не найдя филиала, брал свой по умолчанию — и день открывался в Москве
     * сразу после того, как человек переключился на Питер. Филиал переехал в
     * меню и стал общим для всех разделов; значит и календарь обязан его
     * сохранять, а не сбрасывать каждым кликом по дате.
     */
    const adres = new URLSearchParams(params);
    adres.set('date', den);
    // Филиал берётся из адреса: его туда кладёт переключатель меню, и он же
    // остаётся единственной истиной о том, чей день открыт.
    navigate(`/zapis/den?${adres.toString()}`);
    onClose?.();
  };

  /*
   * ⛔ Ярко, не полупрозрачно (владелец, 09.09: «сам календарь плохо видно,
   * какое-то все мутное и тусклое»): сплошная плашка с рамкой, белые числа,
   * белый жирный заголовок месяца — те же тона, что у выпадашки над ним.
   */
  return (
    <div className="rounded-ops-md bg-white/10 p-3 ring-1 ring-white/25">
      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          aria-label="Прошлый месяц"
          onClick={() => setMesyac(new Date(mesyac.getFullYear(), mesyac.getMonth() - 1, 1))}
          className="flex h-7 w-7 items-center justify-center rounded-ops-sm text-white/80 hover:bg-white/15 hover:text-white"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="whitespace-nowrap text-ops-body font-bold capitalize text-white">
          {/* « г.» отрезается: с крупным шрифтом хвост уезжал второй строкой. */}
          {mesyac.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' }).replace(/\s*г\.$/, '')}
        </span>
        <button
          type="button"
          aria-label="Следующий месяц"
          onClick={() => setMesyac(new Date(mesyac.getFullYear(), mesyac.getMonth() + 1, 1))}
          className="flex h-7 w-7 items-center justify-center rounded-ops-sm text-white/80 hover:bg-white/15 hover:text-white"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 text-center text-ops-micro font-semibold uppercase text-white/60" aria-hidden="true">
        {['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'].map((d) => (
          <span key={d} className="py-0.5">{d}</span>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {kletki.map((chislo, i) =>
          chislo === null ? (
            <span key={`pusto-${i}`} />
          ) : (
            <button
              key={chislo}
              type="button"
              aria-current={kluch(chislo) === vybran ? 'date' : undefined}
              onClick={() => otkrytDen(kluch(chislo))}
              className={[
                'flex h-8 w-full items-center justify-center rounded-ops-sm text-ops-body font-semibold transition-colors',
                kluch(chislo) === vybran
                  ? 'bg-ops-primary font-bold text-white'
                  : kluch(chislo) === segodnyaKluch
                    ? 'font-bold text-white ring-2 ring-inset ring-ops-primary hover:bg-white/15'
                    : 'text-white/90 hover:bg-white/15 hover:text-white',
              ].join(' ')}
            >
              {chislo}
            </button>
          ),
        )}
      </div>
    </div>
  );
}
